import { Link } from 'react-router-dom'
import './index.scss'

const CONTACT_EMAIL = 'lamine.messaci@gmail.com'
const LAST_UPDATED = '23 May 2026'
const SUPPORT_CONTACT_URL =
  '/contact?subject=Welding%20Toolbox%20support%20request'

const supportTopics = [
  {
    frTitle: 'Signaler un bug',
    frBody:
      "Envoyez une description du probleme, le module concerne, votre modele de telephone, la version iOS ou Android, et si possible une capture d'ecran.",
    enTitle: 'Report a bug',
    enBody:
      'Send a description of the issue, the affected module, your phone model, the iOS or Android version, and a screenshot if possible.',
  },
  {
    frTitle: 'Questions et corrections',
    frBody:
      'Si vous trouvez une question, une traduction ou une reponse a corriger dans les QCM, indiquez la categorie et le numero de la question.',
    enTitle: 'Questions and corrections',
    enBody:
      'If you find a quiz question, translation, or answer that should be corrected, please include the category and question number.',
  },
  {
    frTitle: 'Donnees locales',
    frBody:
      "Les historiques, preferences et progressions sont principalement stockes sur votre appareil. En cas de suppression de l'application, ces donnees peuvent etre perdues.",
    enTitle: 'Local data',
    enBody:
      'History, preferences, and progress are mainly stored on your device. If the app is deleted, this local data may be lost.',
  },
]

const privacySections = [
  {
    frTitle: '1. Donnees collectees',
    frBody:
      "Welding Toolbox ne demande pas de compte utilisateur et ne collecte pas volontairement de donnees personnelles telles que nom, adresse, contacts, photos, localisation precise ou informations de paiement.",
    enTitle: '1. Data collected',
    enBody:
      'Welding Toolbox does not require a user account and does not intentionally collect personal data such as name, address, contacts, photos, precise location, or payment information.',
  },
  {
    frTitle: '2. Donnees stockees localement',
    frBody:
      "L'application peut enregistrer localement sur votre appareil vos preferences, votre progression dans les quizzes, vos erreurs a revoir, ainsi que certains historiques ou resultats de calcul. Ces donnees restent sur votre appareil et peuvent etre supprimees en effacant les donnees de l'application ou en la desinstallant.",
    enTitle: '2. Data stored locally',
    enBody:
      'The app may store your preferences, quiz progress, mistakes to review, history, and calculation results locally on your device. This data remains on your device and can be removed by clearing the app data or uninstalling the app.',
  },
  {
    frTitle: '3. Services tiers',
    frBody:
      "L'application peut utiliser des services techniques necessaires a sa distribution, ses mises a jour et son fonctionnement, notamment les plateformes Apple, Google et Expo. Ces services peuvent traiter des donnees techniques selon leurs propres politiques de confidentialite.",
    enTitle: '3. Third-party services',
    enBody:
      'The app may use technical services required for distribution, updates, and operation, including Apple, Google, and Expo platforms. These services may process technical data under their own privacy policies.',
  },
  {
    frTitle: '4. Suivi publicitaire',
    frBody:
      "Welding Toolbox ne vend pas vos donnees personnelles et n'utilise pas vos donnees pour du suivi publicitaire entre applications ou sites web.",
    enTitle: '4. Advertising tracking',
    enBody:
      'Welding Toolbox does not sell your personal data and does not use your data for advertising tracking across apps or websites.',
  },
  {
    frTitle: '5. Contact',
    frBody:
      'Pour toute question relative a la confidentialite ou a vos donnees, contactez-nous par email.',
    enTitle: '5. Contact',
    enBody:
      'For any question about privacy or your data, please contact us by email.',
  },
]

const LanguageBlock = ({ lang, title, body }) => (
  <div className="legal-language-block" lang={lang}>
    <span className="legal-lang-label">{lang === 'fr' ? 'FR' : 'EN'}</span>
    <h2>{title}</h2>
    <p>{body}</p>
  </div>
)

const SupportPage = () => (
  <>
    <p className="legal-eyebrow">Welding Toolbox</p>
    <h1>Support / Assistance</h1>
    <div className="legal-lead-group">
      <p className="legal-lead" lang="fr">
        Besoin d'aide, une anomalie a signaler ou une suggestion pour ameliorer
        l'application ? Utilisez le formulaire de contact.
      </p>
      <p className="legal-lead" lang="en">
        Need help, want to report an issue, or have a suggestion to improve the
        app? Use the contact form.
      </p>
    </div>

    <div className="legal-actions">
      <Link className="legal-primary-link" to={SUPPORT_CONTACT_URL}>
        Contact form / Formulaire de contact
      </Link>
      <a className="legal-secondary-link" href={`mailto:${CONTACT_EMAIL}`}>
        {CONTACT_EMAIL}
      </a>
    </div>

    <div className="legal-grid">
      {supportTopics.map((topic) => (
        <section className="legal-card" key={topic.enTitle}>
          <LanguageBlock
            lang="fr"
            title={topic.frTitle}
            body={topic.frBody}
          />
          <LanguageBlock
            lang="en"
            title={topic.enTitle}
            body={topic.enBody}
          />
        </section>
      ))}
    </div>

    <div className="legal-footer-box">
      <p>
        Privacy Policy / Politique de confidentialite:{' '}
        <Link to="/welding-toolbox-privacy">welding-toolbox-privacy</Link>
      </p>
      <p>Last updated / Derniere mise a jour : {LAST_UPDATED}</p>
    </div>
  </>
)

const PrivacyPage = () => (
  <>
    <p className="legal-eyebrow">Welding Toolbox</p>
    <h1>Privacy Policy / Politique de confidentialite</h1>
    <div className="legal-lead-group">
      <p className="legal-lead" lang="fr">
        Cette politique de confidentialite explique quelles donnees sont
        traitees par l'application Welding Toolbox et comment nous contacter.
      </p>
      <p className="legal-lead" lang="en">
        This privacy policy explains what data is handled by the Welding
        Toolbox app and how to contact us.
      </p>
    </div>
    <p className="legal-date">
      Last updated / Derniere mise a jour : {LAST_UPDATED}
    </p>

    <div className="legal-section-list">
      {privacySections.map((section) => (
        <section key={section.enTitle}>
          <LanguageBlock
            lang="fr"
            title={section.frTitle}
            body={section.frBody}
          />
          <LanguageBlock
            lang="en"
            title={section.enTitle}
            body={section.enBody}
          />
        </section>
      ))}
    </div>

    <div className="legal-footer-box">
      <p>
        Contact:{' '}
        <Link to={SUPPORT_CONTACT_URL}>contact form</Link>
        {' / '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
      </p>
      <p>
        Support / Assistance:{' '}
        <Link to="/welding-toolbox-support">welding-toolbox-support</Link>
      </p>
    </div>
  </>
)

const WeldingToolboxLegal = ({ page }) => {
  const isSupport = page === 'support'

  return (
    <main className="welding-legal-page">
      <div className="legal-shell">
        <Link className="legal-home-link" to="/">
          Lamine Messaci
        </Link>
        {isSupport ? <SupportPage /> : <PrivacyPage />}
      </div>
    </main>
  )
}

export default WeldingToolboxLegal
